import React from 'react';
import { Pressable, View } from 'react-native';
import { cartTotals, fmt, isParcel } from '@restorant/core';
import type { OpenTable } from '@restorant/core';
import { radius, useColors } from '../theme';
import { useItemsLabel, useT, useTableLabel } from '../i18n';
import { Icon } from '../icons/Icon';
import { T } from './Text';

/**
 * One table on the picker. A table with an order in progress shows its running
 * item count and total, so the counter can see at a glance what is live.
 */
export function TableCard({
  tableId,
  open,
  onPress,
}: {
  tableId: string;
  open: OpenTable | null;
  onPress: () => void;
}) {
  const colors = useColors();
  const t = useT();
  const label = useTableLabel();
  const items = useItemsLabel();
  const busy = !!open && open.cart.length > 0;
  const totals = open ? cartTotals(open.cart, open.discount) : null;

  return (
    <Pressable
      onPress={onPress}
      style={({ hovered, pressed }: any) => ({
        flexGrow: 1,
        flexBasis: 150,
        maxWidth: 210,
        minHeight: 112,
        justifyContent: 'space-between',
        gap: 10,
        padding: 14,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: busy ? colors.gold : hovered ? colors.goldDim : colors.line,
        backgroundColor: busy ? colors.surface2 : colors.surface,
        opacity: pressed ? 0.85 : 1,
      })}
    >
      <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Icon
          name={isParcel(tableId) ? 'parcel' : 'table'}
          size={20}
          color={busy ? colors.gold : colors.textFaint}
        />
        {busy ? (
          <View
            style={{
              width: 8,
              height: 8,
              borderRadius: 4,
              backgroundColor: colors.good,
              marginTop: 4,
            }}
          />
        ) : null}
      </View>

      <View style={{ gap: 3 }}>
        <T size={16} weight={700} serif color={busy ? colors.text : colors.textDim}>
          {label(tableId)}
        </T>
        {busy && totals ? (
          <T size={12} weight={600} color={colors.gold}>
            {items(totals.qty)} · {fmt(totals.grand)}
          </T>
        ) : (
          <T size={12} color={colors.textFaint}>
            {t('table_free')}
          </T>
        )}
      </View>
    </Pressable>
  );
}
